/**
 * RevisionPanel — 시험지 변경 이력·되돌리기(계약 §2).
 *
 * 되돌리기도 append-only 다 — 과거 판이 "현재"로 다시 쌓일 뿐 이력이 지워지지 않는다.
 * 이 사실을 적어 두지 않으면 "되돌리면 이후 판이 사라진다"고 겁내서 안 쓰게 된다.
 *
 * **되돌리기가 막히는 경우가 하나 있다**(계약 §1-3, 2026-08-26): 그 과거 판에 **그 뒤로 삭제된
 * 문항**이 들어 있으면 409 `reason:'deleted_items'` 다. 이력이 사라진 것이 아니라 **그 판을
 * 되살릴 수 없다**는 뜻이라, 문구가 이 둘을 섞으면 안 된다 — 그래서 실패 문구를 손으로 쓰지 않고
 * describeApiError 에 맡긴다(사유별 한국어 한 문장이 거기 한 벌로 있다).
 *
 * 이력의 created_at 도 §1-1 로 'Z'(UTC)가 붙어 온다. fmtServerDateTime이 한국 시각으로 표시하고,
 * 존 없는 옛 행도 UTC로 읽는다.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { fetchRevisions, revertPaper, fmtServerDateTime, describeApiError, errorReason, AUTH_REDIRECT } from './examPaperApi';

function RevisionPanel({ paperId, currentRevision = null, onReverted, onBeforeRevert = null, reloadKey = 0 }) {
  const { getAccessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmRev, setConfirmRev] = useState(null);
  const [toast, setToast] = useState(null);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (paperId == null) return;
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const list = await fetchRevisions(paperId, getAccessToken);
      if (seq !== seqRef.current) return;
      setItems(list);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setError(e.message || '변경 이력을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [paperId, getAccessToken]);

  useEffect(() => { setConfirmRev(null); setToast(null); load(); }, [load]);
  // 부모가 시험지를 다시 읽을 때(문항 저장·구성 창 저장·되돌리기) 목록도 따라 읽는다.
  // 이 패널은 탭을 바꿔도 걷히지 않고 숨어 있을 뿐이라, 이게 없으면 처음 열 때 읽은 목록에
  // 머물러 방금 저장한 판이 새로고침 전까지 안 보인다(오너 보고 2026-09-28).
  // 알림은 지우지 않는다 — 되돌리기 성공 문구 바로 뒤에 이 신호가 온다.
  const seenReloadRef = useRef(reloadKey);
  useEffect(() => {
    if (seenReloadRef.current === reloadKey) return; // 처음 읽기는 위 효과가 한다
    seenReloadRef.current = reloadKey;
    load();
  }, [reloadKey, load]);

  const doRevert = async (rev) => {
    if (busy) return;
    // 되돌리면 [문항] 탭이 새 판으로 다시 읽힌다 — 거기 미저장 편집이 있으면 먼저 묻는다
    if (onBeforeRevert && !onBeforeRevert()) return;
    setBusy(rev);
    setToast(null);
    try {
      const d = await revertPaper(paperId, rev, getAccessToken);
      if (d === AUTH_REDIRECT) return; // 401 → /signin 이동 중
      setConfirmRev(null);
      setToast({ type: 'info', text: `rev ${rev} 버전을 현재로 되살렸습니다 — 새 버전으로 쌓였습니다.` });
      await load(); // 이 패널의 목록을 먼저 갱신하고,
      onReverted?.(); // 그 다음 부모에게 알린다(부모 갱신이 이 인스턴스를 걷어내도 메시지가 남게)
    } catch (e) {
      // 계약 §1-3 — 사유(409 deleted_items)를 아는 것은 우리 문구로, 모르는 것은 서버 문구로 흘린다.
      // 확인 상태(confirmRev)는 **풀지 않는다**: 같은 자리에서 왜 막혔는지 읽고 [취소]로 물러나게.
      const text = describeApiError(e, '되돌리지 못했습니다.');
      setToast({
        type: 'error',
        // 삭제된 문항은 이 패널에서 뺄 수 없다 — "못 되살린다"와 "이력이 사라졌다"를 갈라 준다
        text: errorReason(e) === 'deleted_items'
          ? `${text} 이 판은 되살릴 수 없지만 이력에서 사라지지는 않습니다.`
          : text,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-xs font-medium text-gray-500">변경 이력</span>
        <span className="min-w-0 text-[11px] text-gray-400">
          되돌려도 이후 버전은 사라지지 않습니다.(선택한 버전이 새 버전으로 다시 쌓입니다.)
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="이력 새로고침"
          title="새로고침"
          className="ml-auto rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
        >
          <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {error && (
        <div className="space-y-2 p-3">
          <p role="alert" className="text-sm text-danger">{error}</p>
          <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
        </div>
      )}
      {!error && loading && items.length === 0 && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />)}
        </div>
      )}
      {!error && !loading && items.length === 0 && (
        <p className="p-6 text-center text-sm text-gray-400">아직 변경 이력이 없습니다.</p>
      )}

      <ul className="space-y-1">
        {items.map((r, idx) => {
          // 배열 인덱스로 때우면 안 된다 — 그 숫자가 그대로 /revert 로 나가 엉뚱한 판을 되살린다
          const rev = r.revision ?? r.rev ?? null;
          const isCurrent = currentRevision != null && String(rev) === String(currentRevision);
          const count = r.item_count ?? (Array.isArray(r.items) ? r.items.length : null);
          return (
            <li key={rev ?? `idx-${idx}`} className="rounded-xl border border-gray-200 p-2">
              <div className="flex items-center gap-1.5">
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  {rev != null ? `rev ${rev}` : '버전 번호 없음'}
                </span>
                {isCurrent && <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">현재</span>}
                <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{r.title || '제목 없음'}</span>
                <span className="shrink-0 text-[10px] text-gray-400">{fmtServerDateTime(r.created_at || r.updated_at)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[10px] text-gray-400">
                  {count != null ? `문항 ${count}개` : ''}
                  {r.description ? ` · ${r.description}` : ''}
                </span>
                {!isCurrent && rev == null && (
                  <span className="ml-auto text-[10px] text-gray-400" title="응답에 revision 값이 없어 되돌릴 수 없습니다">
                    되돌리기 불가
                  </span>
                )}
                {!isCurrent && rev != null && (
                  confirmRev === rev ? (
                    <span className="ml-auto flex items-center gap-1.5 text-[11px] text-gray-600">
                      이 버전으로?
                      <Button size="xs" onClick={() => doRevert(rev)} disabled={busy != null}>
                        {busy === rev ? '되돌리는 중…' : '되돌리기'}
                      </Button>
                      <Button size="xs" variant="secondary" onClick={() => setConfirmRev(null)} disabled={busy != null}>취소</Button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmRev(rev)}
                      disabled={busy != null}
                      className="ml-auto inline-flex items-center gap-0.5 text-[11px] text-primary underline hover:text-primary/70 disabled:opacity-40"
                    >
                      <RotateCcw className="size-3" />
                      이 버전으로 되돌리기
                    </button>
                  )
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>
    </div>
  );
}

export default RevisionPanel;
